import { useState } from "react";
import { Alert, Box, Button, MenuItem, Select, Snackbar, TextField, Typography } from "@mui/material";
import { useModeration } from "./BlogModeration.hooks";
import PostCard from "../Blog/PostCard";
import PostPreview from "../Blog/PostPreview";
import CategoryManager from "../Blog/CategoryManager";
import { blogPostUrl } from "../Blog/Blog.constants";
import BlogEditor from "../Blog/BlogEditor";

// What the admin must review: the pending edit if there is one, otherwise the post itself.
const versionUnderReview = (post) => ({ ...post, ...(post.pendingChanges || {}) });
const awaitsReview = (post) => post.status === "pending" || !!post.pendingSubmittedAt;

// [stats key, label, the status filter the card switches to when clicked]
const STAT_CARDS = [
  ["total", "Total", "all"], ["published", "Published", "published"], ["pending", "Pending", "pending"],
  ["drafts", "Drafts", "draft"], ["rejected", "Rejected", "rejected"],
];

const BlogModeration = () => {
  const { list, stats, loading, error: loadError, load, publish, reject, unpublish, remove } = useModeration();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [managingCategories, setManagingCategories] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { message, slug }

  const refresh = () => load({ status, search });
  const selectStatus = (value) => { setStatus(value); load({ status: value, search }); };

  const onReject = async (id) => {
    const comment = window.prompt("Reason for rejection (sent to the author):", "");
    if (comment === null) return;
    try { await reject(id, comment); setPreviewing(null); refresh(); }
    catch (e) { setError(e.response?.data?.message || "Failed to reject post."); }
  };
  const onPublish = async (id) => {
    try {
      const res = await publish(id);
      const post = res?.data;
      // Confirm the publish and hand over the live link — the admin had no way
      // to tell it worked (QA 2026-07-28).
      setSuccess({ message: "Post published", slug: post?.slug || "" });
      setPreviewing(null);
      refresh();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to publish post.");
    }
  };

  // Unpublishing sends the post back to the author's drafts — it is not a
  // deletion, so the reason is optional.
  const onUnpublish = async (post) => {
    if (!window.confirm(`Take "${post.title}" off the blog? It goes back to the author's drafts.`)) return;
    const comment = window.prompt("Reason (optional, sent to the author):", "") || "";
    try { await unpublish(post.id, comment); setPreviewing(null); refresh(); }
    catch (e) { setError(e.response?.data?.message || "Failed to unpublish post."); }
  };

  const onDelete = async (post) => {
    const live = post.status === "published" ? " It is currently live on the blog." : "";
    if (!window.confirm(`Delete "${post.title}" permanently?${live} This cannot be undone.`)) return;
    try { await remove(post.id); setPreviewing(null); refresh(); }
    catch (e) { setError(e.response?.data?.message || "Failed to delete post."); }
  };

  if (editing !== null) {
    return <BlogEditor post={editing.id ? editing : null} role="admin"
      onSaved={refresh} onBack={() => { setEditing(null); refresh(); }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h5">Blog Posts</Typography>
          <Typography variant="body2" color="text.secondary">Manage and moderate community blog content</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Button variant="outlined" onClick={() => setManagingCategories(true)}>Manage Categories</Button>
          <Button variant="contained" onClick={() => setEditing({})}>+ New Post</Button>
        </Box>
      </Box>

      {/* The cards double as the status filter — clicking one is the fastest way
          to get to the pending queue (QA 2026-07-27). */}
      <Box sx={{ display: "flex", gap: 2, my: 2 }}>
        {STAT_CARDS.map(([k, label, value]) => {
          const active = status === value;
          return (
            <Box key={k} role="button" tabIndex={0}
              onClick={() => selectStatus(value)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), selectStatus(value))}
              sx={{
                border: "1px solid", borderColor: active ? "primary.main" : "#EEE",
                bgcolor: active ? "action.selected" : "transparent",
                borderRadius: 2, p: 2, minWidth: 90, cursor: "pointer", userSelect: "none",
                transition: "border-color .15s, background-color .15s",
                "&:hover": { borderColor: "primary.main" },
              }}>
              <Typography variant="h6">{stats[k]}</Typography>
              <Typography variant="caption">{label}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField fullWidth size="small" placeholder="Search posts by title, author, or category…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()} />
        <Select size="small" value={status}
          onChange={(e) => selectStatus(e.target.value)}>
          {["all", "published", "pending", "draft", "rejected"].map((s) =>
            <MenuItem key={s} value={s}>{s === "all" ? "All Statuses" : s}</MenuItem>)}
        </Select>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {loading ? <Typography>Loading…</Typography>
        : list.map((p) => (
            <PostCard key={p.id} post={p}
              actions={
                awaitsReview(p) ? (
                  <>
                    {/* Cover image and body must be reviewable before publishing. */}
                    <Button size="small" onClick={() => setPreviewing(p)}>Preview</Button>
                    <Button size="small" color="success" onClick={() => onPublish(p.id)}>
                      {p.pendingSubmittedAt ? "Approve changes" : "Publish"}
                    </Button>
                    <Button size="small" color="error" onClick={() => onReject(p.id)}>Reject</Button>
                    {/* A live post with a queued edit is still live — it must be
                        possible to pull it offline without judging the edit. */}
                    {p.status === "published" && (
                      <Button size="small" color="warning" onClick={() => onUnpublish(p)}>Unpublish</Button>
                    )}
                    <Button size="small" color="error" onClick={() => onDelete(p)}>Delete</Button>
                  </>
                ) : (
                  <>
                    <Button size="small" onClick={() => setPreviewing(p)}>Preview</Button>
                    <Button size="small" onClick={() => setEditing(p)}>View</Button>
                    {p.status === "published" && (
                      <Button size="small" color="warning" onClick={() => onUnpublish(p)}>Unpublish</Button>
                    )}
                    <Button size="small" color="error" onClick={() => onDelete(p)}>Delete</Button>
                  </>
                )
              } />
          ))}

      <PostPreview
        open={!!previewing}
        onClose={() => setPreviewing(null)}
        post={previewing ? versionUnderReview(previewing) : {}}
        actions={previewing && awaitsReview(previewing) ? (
          <>
            <Button onClick={() => setPreviewing(null)}>Close</Button>
            <Button color="error" onClick={() => onReject(previewing.id)}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => onPublish(previewing.id)}>
              {previewing.pendingSubmittedAt ? "Approve changes" : "Publish"}
            </Button>
          </>
        ) : previewing ? (
          <>
            <Button onClick={() => setPreviewing(null)}>Close</Button>
            {previewing.status === "published" && (
              <Button color="warning" onClick={() => onUnpublish(previewing)}>Unpublish</Button>
            )}
            <Button color="error" onClick={() => onDelete(previewing)}>Delete</Button>
          </>
        ) : null}
      />

      <CategoryManager
        open={managingCategories}
        onClose={() => setManagingCategories(false)}
        onChanged={refresh}
      />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError("")}>
        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
      </Snackbar>

      {/* Longer timeout than an error toast: the admin has to be able to click
          through to the article before it disappears. */}
      <Snackbar open={!!success} autoHideDuration={12000} onClose={() => setSuccess(null)}>
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          action={
            success?.slug ? (
              <Button
                size="small"
                color="inherit"
                href={blogPostUrl(success.slug)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View post
              </Button>
            ) : null
          }
        >
          {success?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogModeration;
